/**
 * AdminHomeSections (iter56) — author named product rows that appear on
 * the marketplace-style storefronts (Amazon / Flipkart / Blinkit
 * themes). Each section has:
 *   • title + optional subtitle
 *   • visible-in-themes (empty = all 3)
 *   • a list of products (drag/multi-pick from the tenant's catalog)
 *   • an order that drives top-to-bottom rendering
 *
 * Endpoints:
 *   GET    /api/admin/home-sections
 *   POST   /api/admin/home-sections
 *   PATCH  /api/admin/home-sections/{id}
 *   DELETE /api/admin/home-sections/{id}
 *   POST   /api/admin/home-sections/reorder
 *   GET    /api/admin/home-sections/_products?q=...
 */
import { useEffect, useState, useMemo } from "react";
import {
    Plus, Pencil, Trash2, X, Loader2, GripVertical, ChevronUp,
    ChevronDown, Eye, EyeOff, Search, Check,
} from "lucide-react";
import { toast } from "sonner";
import { api, inr2 } from "../../lib/api";

const SLUGIFY = (s) =>
    String(s || "").toLowerCase().trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 48);

const EMPTY = {
    slug: "",
    title: "",
    subtitle: "",
    order: 100,
    product_ids: [],
    visible_in_themes: [],
    enabled: true,
};

const THEMES = [
    { key: "amazon", label: "Amazon-like" },
    { key: "flipkart", label: "Flipkart-like" },
    { key: "blinkit", label: "Blinkit-like" },
];

function ProductPicker({ value, onChange }) {
    const [products, setProducts] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        api.get("/admin/home-sections/_products", { params: q ? { q } : {} })
            .then(({ data }) => { if (alive) setProducts(data || []); })
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [q]);

    const selected = useMemo(() => new Set(value || []), [value]);
    const productMap = useMemo(
        () => Object.fromEntries(products.map((p) => [p.id, p])),
        [products],
    );

    const toggle = (pid) => {
        const next = new Set(value || []);
        if (next.has(pid)) next.delete(pid); else next.add(pid);
        onChange(Array.from(next));
    };

    const removeAt = (idx) => {
        const next = [...(value || [])];
        next.splice(idx, 1);
        onChange(next);
    };

    const move = (idx, dir) => {
        const next = [...(value || [])];
        const j = idx + dir;
        if (j < 0 || j >= next.length) return;
        [next[idx], next[j]] = [next[j], next[idx]];
        onChange(next);
    };

    return (
        <div className="space-y-3">
            {/* Selected products — ordered list with reorder + remove */}
            <div>
                <div className="flex items-center justify-between mb-1.5">
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        Products in this section ({(value || []).length})
                    </p>
                </div>
                {(!value || value.length === 0) ? (
                    <div className="border border-dashed border-border rounded-xl p-4 text-center text-xs text-muted-foreground">
                        No products yet — tap to add from the list below.
                    </div>
                ) : (
                    <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1" data-testid="hs-selected-list">
                        {(value || []).map((pid, idx) => {
                            const p = productMap[pid];
                            return (
                                <div key={pid} className="flex items-center gap-2 bg-muted/40 border border-border rounded-lg p-1.5"
                                     data-testid={`hs-selected-${pid}`}>
                                    <GripVertical size={14} className="text-muted-foreground" />
                                    <div className="w-9 h-9 bg-card rounded-md overflow-hidden flex-shrink-0">
                                        {p?.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" loading="lazy" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold truncate">{p?.name || pid}</p>
                                        {p && <p className="text-[10px] text-muted-foreground">{inr2(p.selling_price)}</p>}
                                    </div>
                                    <button type="button" onClick={() => move(idx, -1)} className="p-1 hover:bg-muted rounded" disabled={idx === 0}><ChevronUp size={14} /></button>
                                    <button type="button" onClick={() => move(idx, +1)} className="p-1 hover:bg-muted rounded" disabled={idx === (value || []).length - 1}><ChevronDown size={14} /></button>
                                    <button type="button" onClick={() => removeAt(idx)} className="p-1 hover:bg-destructive/10 text-destructive rounded" data-testid={`hs-remove-${pid}`}><X size={14} /></button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Searchable product picker */}
            <div>
                <div className="relative mb-2">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        data-testid="hs-product-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search products in your catalog…"
                        className="w-full bg-card border border-border rounded-xl pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-primary"
                    />
                </div>
                {loading ? (
                    <div className="flex items-center justify-center py-6 text-muted-foreground text-xs"><Loader2 className="animate-spin mr-2" size={14} /> Loading…</div>
                ) : products.length === 0 ? (
                    <div className="text-center py-4 text-xs text-muted-foreground">No products match.</div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1" data-testid="hs-product-grid">
                        {products.map((p) => {
                            const on = selected.has(p.id);
                            return (
                                <button
                                    type="button"
                                    key={p.id}
                                    onClick={() => toggle(p.id)}
                                    data-testid={`hs-toggle-${p.id}`}
                                    className={`flex items-center gap-2 p-2 border rounded-lg transition text-left ${on ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/50"}`}
                                >
                                    <div className="w-10 h-10 bg-muted rounded-md overflow-hidden flex-shrink-0">
                                        {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" loading="lazy" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold truncate">{p.name}</p>
                                        <p className="text-[10px] text-muted-foreground">{inr2(p.selling_price)}{p.mrp > p.selling_price ? ` · MRP ${inr2(p.mrp)}` : ""}</p>
                                    </div>
                                    {on && <Check size={16} className="text-primary flex-shrink-0" />}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function SectionFormModal({ open, onClose, edit, onSave, busy }) {
    const [form, setForm] = useState(EMPTY);

    useEffect(() => {
        if (edit) {
            setForm({
                ...EMPTY,
                ...edit,
                product_ids: edit.product_ids || [],
                visible_in_themes: edit.visible_in_themes || [],
            });
        } else {
            setForm(EMPTY);
        }
    }, [edit, open]);

    if (!open) return null;

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setTitle = (t) => {
        setForm((f) => ({
            ...f, title: t,
            slug: (!edit && (!f.slug || f.slug === SLUGIFY(f.title))) ? SLUGIFY(t) : f.slug,
        }));
    };

    const toggleTheme = (k) => {
        const cur = new Set(form.visible_in_themes || []);
        if (cur.has(k)) cur.delete(k); else cur.add(k);
        set("visible_in_themes", Array.from(cur));
    };

    const submit = (e) => {
        e.preventDefault();
        if (!form.title.trim()) { toast.error("Title is required"); return; }
        if (!form.slug.trim()) { toast.error("Slug is required"); return; }
        onSave(form);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" data-testid="hs-form-modal">
            <div className="bg-card rounded-2xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-card border-b border-border px-5 py-3 flex items-center justify-between z-10">
                    <h3 className="text-lg font-bold">{edit ? "Edit Home Section" : "New Home Section"}</h3>
                    <button onClick={onClose} className="p-1.5 hover:bg-muted rounded-lg" data-testid="hs-form-close"><X size={18} /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Title</label>
                            <input data-testid="hs-title-input" value={form.title} onChange={(e) => setTitle(e.target.value)} placeholder="Deal of the Day"
                                   className="w-full mt-1 bg-card border border-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">URL slug</label>
                            <input data-testid="hs-slug-input" value={form.slug} onChange={(e) => set("slug", SLUGIFY(e.target.value))} placeholder="deal-of-the-day"
                                   className="w-full mt-1 bg-card border border-border rounded-xl px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Subtitle (optional)</label>
                        <input data-testid="hs-subtitle-input" value={form.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} placeholder="Up to 60% off · Today only"
                               className="w-full mt-1 bg-card border border-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Order</label>
                            <input data-testid="hs-order-input" type="number" value={form.order} onChange={(e) => set("order", Number(e.target.value || 0))}
                                   className="w-full mt-1 bg-card border border-border rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary" />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Enabled</label>
                            <button type="button" onClick={() => set("enabled", !form.enabled)} data-testid="hs-enabled-toggle"
                                className={`w-full mt-1 px-3 py-2 rounded-xl text-sm font-semibold border ${form.enabled ? "bg-primary text-primary-foreground border-primary" : "bg-muted text-muted-foreground border-border"}`}>
                                {form.enabled ? "Enabled" : "Disabled"}
                            </button>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Visible in themes</label>
                        <p className="text-[11px] text-muted-foreground mb-1.5">Leave all unchecked to show in all 3 marketplace themes.</p>
                        <div className="flex gap-2 flex-wrap">
                            {THEMES.map((t) => {
                                const on = (form.visible_in_themes || []).includes(t.key);
                                return (
                                    <button type="button" key={t.key} onClick={() => toggleTheme(t.key)} data-testid={`hs-theme-${t.key}`}
                                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${on ? "bg-primary text-primary-foreground border-primary" : "bg-card text-foreground border-border hover:border-primary"}`}>
                                        {t.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="border-t border-border pt-4">
                        <ProductPicker value={form.product_ids} onChange={(v) => set("product_ids", v)} />
                    </div>
                    <div className="sticky bottom-0 bg-card pt-3 border-t border-border flex items-center justify-end gap-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border border-border text-sm font-semibold">Cancel</button>
                        <button type="submit" disabled={busy} data-testid="hs-save-btn" className="bg-primary text-primary-foreground rounded-xl px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
                            {busy && <Loader2 size={14} className="animate-spin" />} {edit ? "Save changes" : "Create section"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function AdminHomeSections() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/home-sections");
            setRows(data || []);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const onCreate = () => { setEdit(null); setOpen(true); };
    const onEdit = (s) => { setEdit(s); setOpen(true); };

    const onSave = async (form) => {
        setBusy(true);
        try {
            if (edit) {
                await api.patch(`/admin/home-sections/${edit.id}`, form);
                toast.success("Section updated");
            } else {
                await api.post("/admin/home-sections", form);
                toast.success("Section created");
            }
            setOpen(false);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    };

    const onDelete = async (s) => {
        if (!window.confirm(`Delete the "${s.title}" section?`)) return;
        try {
            await api.delete(`/admin/home-sections/${s.id}`);
            toast.success("Section deleted");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const onToggleEnabled = async (s) => {
        try {
            await api.patch(`/admin/home-sections/${s.id}`, { enabled: !s.enabled });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        }
    };

    return (
        <div className="p-4 sm:p-6 space-y-5" data-testid="admin-home-sections">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold">Home Sections</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Product rows shown on the marketplace storefront (Amazon / Flipkart / Blinkit themes).
                    </p>
                </div>
                <button onClick={onCreate} data-testid="hs-new-btn"
                    className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2">
                    <Plus size={16} /> New section
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground"><Loader2 className="animate-spin mr-2" /> Loading…</div>
            ) : rows.length === 0 ? (
                <div className="bg-card border border-dashed border-border rounded-2xl p-10 text-center" data-testid="hs-empty">
                    <p className="text-base font-bold mb-1">No sections yet</p>
                    <p className="text-sm text-muted-foreground mb-4">
                        Create your first section to populate the marketplace home page.
                    </p>
                    <button onClick={onCreate} className="bg-primary text-primary-foreground rounded-xl px-4 py-2 text-sm font-semibold inline-flex items-center gap-2"><Plus size={16} /> Create section</button>
                </div>
            ) : (
                <div className="space-y-3" data-testid="hs-list">
                    {rows.map((s) => (
                        <div key={s.id} className="bg-card border border-border rounded-2xl p-4 flex items-start gap-3 flex-wrap" data-testid={`hs-row-${s.id}`}>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="text-base font-bold">{s.title}</h3>
                                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-muted text-muted-foreground">{s.slug}</span>
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-muted">Order {s.order}</span>
                                    {!s.enabled && <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-destructive/10 text-destructive">Disabled</span>}
                                </div>
                                {s.subtitle && <p className="text-xs text-muted-foreground mt-1">{s.subtitle}</p>}
                                <div className="flex items-center gap-2 mt-2 flex-wrap text-[11px] text-muted-foreground">
                                    <span>{(s.product_ids || []).length} products</span>
                                    {(s.visible_in_themes || []).length > 0 && (
                                        <span>· Visible in: {(s.visible_in_themes || []).join(", ")}</span>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <button onClick={() => onToggleEnabled(s)} title={s.enabled ? "Disable" : "Enable"}
                                    data-testid={`hs-toggle-enable-${s.id}`}
                                    className="p-2 hover:bg-muted rounded-lg">
                                    {s.enabled ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                                <button onClick={() => onEdit(s)} data-testid={`hs-edit-${s.id}`} className="p-2 hover:bg-muted rounded-lg"><Pencil size={16} /></button>
                                <button onClick={() => onDelete(s)} data-testid={`hs-delete-${s.id}`} className="p-2 hover:bg-destructive/10 text-destructive rounded-lg"><Trash2 size={16} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <SectionFormModal
                open={open}
                onClose={() => setOpen(false)}
                edit={edit}
                onSave={onSave}
                busy={busy}
            />
        </div>
    );
}
